import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Plus,
    Search,
    ImageIcon,
    Video,
    Video as VideoIcon,
    Trash2,
    Edit2,
    Eye,
    EyeOff,
    Loader2,
    X,
    Play,
    Maximize2,
    AlertCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { GalleryServices } from "../../services/admin/GalleryServices";
import NotificationStack from "../../components/Notification";
import ConfirmationModal from "../../components/admin/ConfirmationModal";


const getYouTubeID = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

// ─── Video Processing Indicator ─────────────────────────────
const VideoProcessingIndicator = ({ item, onStatusCheck }) => {
    const [secondsLeft, setSecondsLeft] = useState(45);
    const [isChecking, setIsChecking] = useState(false);
    const pollIntervalRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    useEffect(() => {
        // Video is ready — stop everything
        if (item.url && item.url !== "processing") {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            return;
        }

        // Poll every 5 seconds
        pollIntervalRef.current = setInterval(async () => {
            const updated = await onStatusCheck(item.id);
            if (updated?.url && updated.url !== "processing") {
                clearInterval(pollIntervalRef.current);
                clearInterval(countdownIntervalRef.current);
            }
        }, 5000);

        // Countdown timer
        countdownIntervalRef.current = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    clearInterval(pollIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleManualCheck = async () => {
        setIsChecking(true);
        await onStatusCheck(item.id);
        setIsChecking(false);
    };

    const progress = ((45 - secondsLeft) / 45) * 100;

    return (
        <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-4 text-center z-10">
            <div className="w-16 h-16 mb-4 relative">
                <svg className="transform -rotate-90 w-16 h-16">
                    <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="rgba(196, 164, 132, 0.1)"
                        strokeWidth="4"
                        fill="none"
                    />
                    <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#C4A484"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
                        className="transition-all duration-300"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <VideoIcon size={24} className="text-[#C4A484]" />
                </div>
            </div>
            <p className="text-white text-sm font-medium mb-1" style={{ fontFamily: "Playfair Display, serif" }}>Processing Video</p>
            <p className="text-white/60 text-xs uppercase tracking-widest font-bold mb-4">{secondsLeft}s remaining</p>
            <button
                onClick={handleManualCheck}
                disabled={isChecking}
                className="px-5 py-2.5 bg-[#C4A484] hover:bg-[#B39373] disabled:bg-[#C4A484]/50 text-black text-xs font-bold tracking-widest uppercase rounded-lg transition-all active:scale-95 flex items-center gap-2 shadow-lg shadow-[#C4A484]/10"
            >
                {isChecking ? <Loader2 size={12} className="animate-spin" /> : null}
                {isChecking ? "CHECKING..." : "CHECK STATUS"}
            </button>
        </div>
    );
};


// ─── Media Lightbox Modal ───────────────────────────────────
const MediaLightbox = ({ item, onClose }) => {
    if (!item) return null;

    const youtubeId = item.youtube_link ? getYouTubeID(item.youtube_link) : null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
            onClick={onClose}
        >
            {/* Close Button */}
            <button
                onClick={onClose}
                className="absolute top-6 right-6 z-50 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all backdrop-blur-md"
            >
                <X size={24} />
            </button>

            {/* Caption */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 text-center">
                <p className="text-white text-lg font-medium mb-1" style={{ fontFamily: "Playfair Display, serif" }}>
                    {item.caption}
                </p>
                <p className="text-white/60 text-xs uppercase tracking-widest font-bold">
                    {item.type === "video" ? (youtubeId ? "YouTube Video" : "Video") : "Image"} • Added {item.created_at}
                </p>
            </div>

            {/* Media Content */}
            <div
                className={item.type === "video" && youtubeId ? "w-full max-w-5xl aspect-video relative" : "max-w-[90vw] max-h-[80vh] relative"}
                onClick={(e) => e.stopPropagation()}
            >
                {item.type === "video" ? (
                    youtubeId ? (
                        <iframe
                            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=0&controls=0&rel=0&modestbranding=1&showinfo=0&fs=0`}
                            className="w-full h-full rounded-xl shadow-2xl"
                            title={item.caption}
                            style={{ pointerEvents: 'none' }}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <video
                            src={item.url}
                            controls
                            autoPlay
                            className="max-w-full max-h-[80vh] rounded-xl shadow-2xl"
                        />
                    )
                ) : (
                    <img
                        src={item.url}
                        alt={item.caption}
                        className="max-w-full max-h-[80vh] rounded-xl shadow-2xl object-contain"
                    />
                )}
            </div>
        </div>
    );
};


// ─── Gallery Page ───────────────────────────────────────────
export default function GalleryPage() {
    const navigate = useNavigate();
    const [galleryList, setGalleryList] = useState([]);
    const [filter, setFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);

    // UI State
    const [notifications, setNotifications] = useState([]);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null });
    const [lightboxItem, setLightboxItem] = useState(null);


    const addNotification = useCallback((type, message, title) => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, type, message, title }]);
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    }, []);

    // Close lightbox on Escape
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") setLightboxItem(null);
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, []);

    const loadGallery = useCallback(async () => {
        try {
            const data = await GalleryServices.getAllItems();
            const mapped = data.map(item => ({
                id: item.id,
                type: item.media_type,
                url: item.url,
                youtube_link: item.youtube_link || null,
                caption: item.title,
                status: item.status || "active",
                downloadable: item.downloadable,
                video_status: item.video_status,
                duration: item.duration,
                created_at: item.created_at
                    ? new Date(item.created_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric"
                    })
                    : "Unknown"
            }));
            setGalleryList(mapped);
        } catch (error) {
            console.error("Failed to load gallery", error);
            addNotification("failed", "Failed to load gallery items.", "Error");
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        loadGallery();
    }, [loadGallery]);

    // Polls a single gallery item and updates state — used by VideoProcessingIndicator
    const handleVideoStatusCheck = async (itemId) => {
        try {
            const fresh = await GalleryServices.getItemById(itemId);
            if (fresh) {
                const mapped = {
                    id: fresh.id,
                    type: fresh.media_type,
                    url: fresh.url,
                    youtube_link: fresh.youtube_link || null,
                    caption: fresh.title,
                    status: fresh.status || "active",
                    downloadable: fresh.downloadable,
                    video_status: fresh.video_status,
                    created_at: fresh.created_at
                        ? new Date(fresh.created_at).toLocaleDateString("en-US", {
                            month: "short", day: "numeric", year: "numeric"
                        })
                        : "Unknown"
                };

                setGalleryList(prev =>
                    prev.map(g => g.id === itemId ? mapped : g)
                );

                return mapped;
            }
        } catch (err) {
            console.error("Status check failed", err);
        }
        return null;
    };

    const toggleStatus = async (id) => {
        const item = galleryList.find(g => g.id === id);
        if (!item) return;

        const newStatus = item.status === "active" ? "disabled" : "active";

        // Optimistic update
        setGalleryList(prev => prev.map(g =>
            g.id === id ? { ...g, status: newStatus } : g
        ));

        try {
            await GalleryServices.updateItem(id, { status: newStatus });
            addNotification("success", `Item ${newStatus === 'active' ? 'activated' : 'disabled'} successfully.`, "Success");
        } catch (error) {
            console.error("Failed to toggle status", error);
            addNotification("failed", "Failed to update status. Please try again.", "Error");
            // Revert
            setGalleryList(prev => prev.map(g =>
                g.id === id ? { ...g, status: item.status } : g
            ));
        }
    };

    const confirmDelete = async () => {
        if (!deleteModal.itemId) return;

        try {
            await GalleryServices.deleteItem(deleteModal.itemId);
            setGalleryList(prev => prev.filter(item => item.id !== deleteModal.itemId));
            addNotification("success", "Media item deleted successfully.", "Deleted");
        } catch (error) {
            console.error("Delete failed", error);
            addNotification("failed", "Failed to delete item. Please try again.", "Error");
        } finally {
            setDeleteModal({ isOpen: false, itemId: null });
        }
    };

    const openDeleteModal = (id) => {
        setDeleteModal({ isOpen: true, itemId: id });
    };

    const filteredGallery = (Array.isArray(galleryList) ? galleryList : []).filter(item => {
        const matchesFilter = filter === "all" || item.type === filter;
        const matchesSearch = (item.caption || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        return matchesFilter && matchesSearch;
    });

    // Helper: is this item currently processing a video?
    const isVideoProcessing = (item) => {
        if (item.youtube_link) return false; // YouTube videos are always ready
        return item.type === "video" && (
            item.video_status === "processing" ||
            item.url === "processing" ||
            !item.url
        );
    };

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                <NotificationStack notifications={notifications} onClose={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />

                <ConfirmationModal
                    isOpen={deleteModal.isOpen}
                    onClose={() => setDeleteModal({ isOpen: false, itemId: null })}
                    onConfirm={confirmDelete}
                    title="Delete Media"
                    message="Are you sure you want to delete this specific media item from the gallery? This action cannot be undone."
                    confirmText="Delete"
                    isDanger={true}
                />

                {/* Lightbox Modal */}
                {lightboxItem && (
                    <MediaLightbox
                        item={lightboxItem}
                        onClose={() => setLightboxItem(null)}
                    />
                )}

                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Gallery Management
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Upload and organize photos and videos for your artist gallery.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate("/admin/gallery/add")}
                        className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                    >
                        <Plus size={18} /> UPLOAD MEDIA
                    </button>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-8 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/30" size={18} />
                        <input
                            type="text"
                            placeholder="Search captions..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex bg-gray-100 dark:bg-zinc-800 p-1 rounded-xl w-full md:w-auto">
                        <button
                            onClick={() => setFilter("all")}
                            className={`flex-1 md:flex-none px-6 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all ${filter === "all" ? "bg-white dark:bg-[#1A1A1A] text-black shadow-sm" : "text-black dark:text-white/40"}`}
                        >
                            ALL
                        </button>
                        <button
                            onClick={() => setFilter("image")}
                            className={`flex-1 md:flex-none px-6 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all ${filter === "image" ? "bg-white dark:bg-[#1A1A1A] text-black shadow-sm" : "text-black dark:text-white/40"}`}
                        >
                            IMAGES
                        </button>
                        <button
                            onClick={() => setFilter("video")}
                            className={`flex-1 md:flex-none px-6 py-1.5 rounded-lg text-xs font-bold tracking-wider transition-all ${filter === "video" ? "bg-white dark:bg-[#1A1A1A] text-black shadow-sm" : "text-black dark:text-white/40"}`}
                        >
                            VIDEOS
                        </button>
                    </div>
                </div>

                {/* Gallery Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

                    {/* Loading State */}
                    {loading && (
                        <div className="col-span-full py-20 bg-white dark:bg-[#1A1A1A] rounded-2xl border border-dashed border-black/10 text-center">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                                <Loader2 className="text-black/40" size={32} />
                            </div>
                            <h3 className="text-lg font-medium text-black">Loading Gallery...</h3>
                        </div>
                    )}

                    {!loading && filteredGallery.map((item) => (
                        <div key={item.id} className="group relative bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                            {/* Media Container */}
                            <div className="aspect-square relative overflow-hidden bg-gray-100 dark:bg-zinc-800">

                                {/* Video Processing Indicator (overlays the card) */}
                                {isVideoProcessing(item) && (
                                    <VideoProcessingIndicator
                                        item={item}
                                        onStatusCheck={handleVideoStatusCheck}
                                    />
                                )}

                                {/* Media preview — IMAGE */}
                                {!isVideoProcessing(item) && item.type === "image" && (
                                    <img
                                        src={item.url}
                                        alt={item.caption}
                                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${item.status === 'disabled' ? 'opacity-50 grayscale' : ''}`}
                                    />
                                )}

                                {/* Media preview — VIDEO */}
                                {!isVideoProcessing(item) && item.type === "video" && (
                                    <div className="relative w-full h-full">
                                        {item.youtube_link ? (
                                            <img
                                                src={`https://img.youtube.com/vi/${getYouTubeID(item.youtube_link)}/maxresdefault.jpg`}
                                                alt={item.caption}
                                                className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${item.status === 'disabled' ? 'opacity-50 grayscale' : ''}`}
                                                onError={(e) => { e.target.src = `https://img.youtube.com/vi/${getYouTubeID(item.youtube_link)}/hqdefault.jpg`; }}
                                            />
                                        ) : (
                                            <video
                                                src={item.url}
                                                muted
                                                playsInline
                                                preload="metadata"
                                                className={`w-full h-full object-cover ${item.status === 'disabled' ? 'opacity-50 grayscale' : ''}`}
                                                onMouseEnter={(e) => { try { e.target.play(); } catch { } }}
                                                onMouseLeave={(e) => { try { e.target.pause(); e.target.currentTime = 0; } catch { } }}
                                            />
                                        )}
                                        {/* Play icon overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity duration-300">
                                            <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center backdrop-blur-sm">
                                                <Play size={20} className="text-white ml-0.5" fill="white" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Type Icon Overlay */}
                                <div className="absolute top-3 left-3 bg-white/90 dark:bg-black/90 p-2 rounded-lg backdrop-blur-md shadow-sm">
                                    {item.type === "video" ? <Video size={14} className="text-jazz-brown" /> : <ImageIcon size={14} className="text-jazz-brown" />}
                                </div>

                                {/* Video Duration Badge */}
                                {item.type === "video" && item.duration && (
                                    <div className="absolute top-3 right-3 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                                        {item.duration}
                                    </div>
                                )}

                                {/* Status Badges */}
                                <div className="absolute bottom-3 left-3 flex gap-2">
                                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider backdrop-blur-md ${item.status === 'active' ? 'bg-green-500/80 text-white' : 'bg-red-500/80 text-white'}`}>
                                        {item.status}
                                    </span>
                                    {item.downloadable === false && (
                                        <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider backdrop-blur-md bg-amber-500/80 text-white flex items-center gap-1">
                                            <AlertCircle size={10} /> NO DL
                                        </span>
                                    )}
                                </div>

                                {/* Actions Overlay (only show when not processing) */}
                                {!isVideoProcessing(item) && (
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                                        {/* View / Expand button */}
                                        <button
                                            onClick={() => setLightboxItem(item)}
                                            className="p-3 bg-white text-black rounded-full hover:bg-jazz-brown hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0"
                                            title="View Full"
                                        >
                                            <Maximize2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => toggleStatus(item.id)}
                                            className="p-3 bg-white text-black rounded-full hover:bg-jazz-brown hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 delay-75"
                                            title={item.status === 'active' ? "Disable" : "Enable"}
                                        >
                                            {item.status === 'active' ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                        <button
                                            onClick={() => navigate(`/admin/gallery/edit/${item.id}`)}
                                            className="p-3 bg-white text-black rounded-full hover:bg-jazz-brown hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 delay-100"
                                            title="Edit"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => openDeleteModal(item.id)}
                                            className="p-3 bg-white text-red-500 rounded-full hover:bg-red-500 hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 delay-150"
                                            title="Delete"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Metadata */}
                            <div className="p-4">
                                <p className="text-sm font-medium text-black dark:text-white truncate" style={{ fontFamily: "Crimson Pro, serif" }}>
                                    {item.caption}
                                </p>
                                <p className="text-xs text-black dark:text-white/40 uppercase tracking-widest mt-1 font-bold">
                                    Added {item.created_at}
                                </p>
                            </div>
                        </div>
                    ))}

                    {/* Empty State */}
                    {!loading && filteredGallery.length === 0 && (
                        <div className="col-span-full py-20 bg-white dark:bg-[#1A1A1A] rounded-2xl border border-dashed border-black/10 text-center">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <ImageIcon className="text-black/40" size={32} />
                            </div>
                            <h3 className="text-lg font-medium text-black">No media found</h3>
                            <p className="text-sm text-black dark:text-white/40 mt-1">Try adjusting your filters or search term.</p>
                        </div>
                    )}
            </div>
        </div>
    );
}
